import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../../components/Navbar';
import { BackButton } from '../../components/BackButton';
import { formatOrderStatus } from '../../utils/orderStatus';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { NotificationService } from '../../utils/notifications';
import { getETAToAddress } from '../../utils/mapbox';
import { useSSE } from '../../hooks/useSSE';
import { usePushSubscribe } from '../../hooks/usePushSubscribe';
import { useFlag } from '../../context/FeatureFlagsContext';
import { logEvent } from '../../utils/eventsLogger';
import { 
  ArrowLeft, 
  Package, 
  MapPin, 
  Clock, 
  CreditCard,
  Truck,
  CheckCircle,
  XCircle,
  Navigation,
  Bell,
  BellOff,
  RefreshCw,
  Route,
  Radio
} from 'lucide-react';
import { OrderFeeRows, slotWhen } from '../../components/OrderSlotSummary';
import { ReviewItemTag } from '../../components/reviews/ReviewItemTag';
import { toast } from 'sonner';

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const orderStatuses = [
  { key: 'pending', label: 'Pending', icon: Clock },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle },
  { key: 'preparing', label: 'Preparing', icon: Package },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle }
];

export function OrderDetailsPage() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifStatus, setNotifStatus] = useState(() => NotificationService.getPermissionStatus());
  const [etaData, setEtaData] = useState(null);
  const [etaLoading, setEtaLoading] = useState(false);
  const [lastLocationUpdate, setLastLocationUpdate] = useState(null);
  const [sseConnected, setSseConnected] = useState(false);
  const showDriverLocation = useFlag('customer.driver_location', true);
  const { ensureSubscribed: ensurePushSubscribed } = usePushSubscribe();
  const previousStatusRef = useRef(null);
  const lastDriverLocationRef = useRef(null);

  // Calculate ETA using Mapbox
  const calculateETA = useCallback(async (driverLocation, deliveryAddress) => {
    if (!driverLocation || !deliveryAddress) return;
    
    // Only recalculate if driver location actually changed
    const locKey = `${Number(driverLocation.lat ?? 0).toFixed(4)},${Number(driverLocation.lng ?? 0).toFixed(4)}`;
    if (lastDriverLocationRef.current === locKey) return;
    lastDriverLocationRef.current = locKey;
    
    setEtaLoading(true);
    try {
      const eta = await getETAToAddress(driverLocation, deliveryAddress);
      if (eta) {
        setEtaData(eta);
      }
    } catch (err) {
      console.error('ETA calculation error:', err);
    } finally {
      setEtaLoading(false);
    }
  }, []);

  const fetchOrder = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/orders/${orderId}`, { withCredentials: true });

      // Phase 5.9 — log order tracking view (once per session per order)
      logEvent('order.tracked_viewed', {
        order_id: data.id,
        status: data.status,
      }, { dedupeKey: data.id });

      // Check if status changed and send notification
      if (previousStatusRef.current && previousStatusRef.current !== data.status) {
        NotificationService.notifyOrderStatus(data.id, data.status, data.driver_name);
        toast.success(`Order status updated: ${formatOrderStatus(data.status)}`);
      }
      previousStatusRef.current = data.status;
      
      // Track location updates and calculate ETA
      if (data.driver_location) {
        setLastLocationUpdate(new Date());
        // Calculate real ETA if out for delivery
        if (data.status === 'out_for_delivery') {
          calculateETA(data.driver_location, data.delivery_address);
        }
      }
      
      setOrder(data);
    } catch (err) {
      toast.error('Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [orderId, calculateETA]);

  useEffect(() => {
    fetchOrder();
    // Fallback poll every 30s — SSE is primary
    const interval = setInterval(() => {
      if (order && !['delivered', 'cancelled'].includes(order.status)) {
        fetchOrder();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchOrder, order?.status]);

  // Apply a status change received via SSE
  const applyStatusChange = useCallback((newStatus) => {
    setOrder(prev => {
      if (!prev) return prev;
      if (prev.status === newStatus) return prev;
      // Fire notification + toast for meaningful transitions
      NotificationService.notifyOrderStatus(prev.id, newStatus, prev.driver_name);
      toast.success(`Order status: ${newStatus.replace('_', ' ').toUpperCase()}`);
      return { ...prev, status: newStatus, updated_at: new Date().toISOString() };
    });
  }, []);

  // Apply a driver-assignment patch received via SSE
  const applyDriverAssigned = useCallback((driverId, driverName) => {
    setOrder(prev => {
      if (!prev) return prev;
      if (prev.driver_id === driverId) return prev;
      toast.info(`Driver assigned: ${driverName}`);
      return { ...prev, driver_id: driverId, driver_name: driverName };
    });
  }, []);

  // Apply a live driver location update received via SSE
  const applyDriverLocation = useCallback((location) => {
    setLastLocationUpdate(new Date());
    setOrder(prev => {
      if (!prev) return prev;
      const nextOrder = { ...prev, driver_location: location };
      // Trigger ETA refresh if out for delivery
      if (nextOrder.status === 'out_for_delivery') {
        calculateETA(location, nextOrder.delivery_address);
      }
      return nextOrder;
    });
  }, [calculateETA]);

  // Real-time order updates via SSE (scoped to this customer's orders)
  useSSE(useCallback((evt) => {
    if (!evt || !evt.type) return;
    setSseConnected(true);

    switch (evt.type) {
      case 'stream.ready':
        break;
      case 'order.status_updated':
        if (evt.order_id === orderId) applyStatusChange(evt.status);
        break;
      case 'order.assigned':
        if (evt.order_id === orderId) applyDriverAssigned(evt.driver_id, evt.driver_name);
        break;
      case 'order.location_updated':
        if (evt.order_id === orderId) applyDriverLocation(evt.location);
        break;
      case 'order.items_updated':
        if (evt.order_id === orderId) {
          setOrder(prev => prev ? { ...prev, items: evt.items, total: evt.total } : prev);
          toast.info('Your order was updated by the dispatcher');
        }
        break;
      default:
        break;
    }
  }, [orderId, applyStatusChange, applyDriverAssigned, applyDriverLocation]));

  // Reset ETA when order status changes away from out_for_delivery
  useEffect(() => {
    if (order?.status !== 'out_for_delivery') {
      setEtaData(null);
      lastDriverLocationRef.current = null;
    }
  }, [order?.status]);

  // If notifications were granted on a prior visit, refresh the push
  // subscription on mount so the backend has a fresh endpoint to deliver to.
  useEffect(() => {
    if (notifStatus === 'granted') {
      ensurePushSubscribed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifStatus]);

  const handleEnableNotifications = async () => {
    const current = NotificationService.getPermissionStatus();

    if (current === 'ios-needs-pwa') {
      toast.info('Add to Home Screen for alerts', {
        description: 'Tap Share → "Add to Home Screen" to enable background notifications on iPhone. Live updates & sounds still work in this tab.',
        duration: 8000,
      });
      return;
    }
    if (current === 'unsupported') {
      toast.info('Notifications not supported in this browser', {
        description: 'Live updates still stream to this page — you\'ll see status changes in real time.',
        duration: 6000,
      });
      return;
    }
    if (current === 'denied') {
      toast.error('Notifications are blocked in browser settings', {
        description: 'Open this site\'s Settings (Aa icon in Safari → Website Settings, or browser lock icon → Site settings) to re-allow notifications.',
        duration: 9000,
      });
      return;
    }

    const granted = await NotificationService.requestPermission();
    setNotifStatus(NotificationService.getPermissionStatus());
    if (granted) {
      // Register the browser with our push gateway too — this is what
      // enables alerts when the tab is closed / device asleep.
      const pushOk = await ensurePushSubscribed();
      toast.success(
        pushOk
          ? 'Notifications enabled. You\'ll get pushes even when this tab is closed.'
          : 'Notifications enabled in this tab. (Background push could not register on this device.)'
      );
    } else {
      toast.error('Notifications permission not granted');
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    try {
      await axios.patch(`${API_URL}/api/orders/${orderId}/status`, { status: 'cancelled' }, { withCredentials: true });
      toast.success('Order cancelled');
      fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-[rgba(255,159,10,0.1)] text-[#FF9F0A] border-[#FF9F0A]',
      confirmed: 'bg-[rgba(0,240,255,0.1)] text-[#00F0FF] border-[#00F0FF]',
      preparing: 'bg-[rgba(0,240,255,0.1)] text-[#00F0FF] border-[#00F0FF]',
      out_for_delivery: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]',
      delivered: 'bg-[rgba(57,255,20,0.1)] text-[#39FF14] border-[#39FF14]',
      cancelled: 'bg-[rgba(255,59,48,0.1)] text-[#FF3B30] border-[#FF3B30]'
    };
    return styles[status] || styles.pending;
  };

  const getCurrentStatusIndex = () => {
    if (order?.status === 'cancelled') return -1;
    return orderStatuses.findIndex(s => s.key === order?.status);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050003]">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-[#39FF14] font-mono">
            <span className="cursor-blink">█</span> LOADING_ORDER...
          </div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-[#050003]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <XCircle className="w-16 h-16 text-[#FF3B30] mx-auto mb-4" />
          <h1 className="text-[#39FF14] font-mono text-2xl font-bold mb-2">ORDER_NOT_FOUND</h1>
          <Link to="/customer/orders">
            <Button className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              BACK TO ORDERS
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050003]" data-testid="order-details-page">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <BackButton to="/customer/orders" />
          <div>
            <h1 className="text-[#39FF14] font-mono text-xl font-bold neon-text">
              ORDER #{order.id.slice(0, 8).toUpperCase()}
            </h1>
            <p className="text-[#4B6B52] font-mono text-sm">
              {new Date(order.created_at).toLocaleString()}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            {/* Live stream indicator */}
            {!['delivered', 'cancelled'].includes(order.status) && (
              <span
                className={`flex items-center gap-1 px-2 py-1 border font-mono text-[10px] uppercase ${
                  sseConnected
                    ? 'border-[#39FF14] text-[#39FF14] bg-[rgba(57,255,20,0.08)]'
                    : 'border-[#4B6B52] text-[#4B6B52]'
                }`}
                data-testid="customer-sse-status"
                title={sseConnected ? 'Live updates connected' : 'Connecting…'}
              >
                <Radio className={`w-3 h-3 ${sseConnected ? 'animate-pulse' : ''}`} />
                {sseConnected ? 'LIVE' : '...'}
              </span>
            )}
            {/* Notification Toggle — platform-aware */}
            {!['delivered', 'cancelled'].includes(order.status) && (() => {
              // Decide button look + label
              let label = 'ENABLE ALERTS';
              let Icon = BellOff;
              let colorCls = 'border-[#FF9F0A] text-[#FF9F0A]';
              if (notifStatus === 'granted') {
                label = 'ALERTS ON';
                Icon = Bell;
                colorCls = 'border-[#39FF14] text-[#39FF14]';
              } else if (notifStatus === 'denied') {
                label = 'ALERTS BLOCKED';
                Icon = BellOff;
                colorCls = 'border-[#FF3B30] text-[#FF3B30]';
              } else if (notifStatus === 'ios-needs-pwa') {
                label = 'ADD TO HOME SCREEN';
                Icon = BellOff;
                colorCls = 'border-[#00F0FF] text-[#00F0FF]';
              } else if (notifStatus === 'unsupported') {
                label = 'ALERTS N/A';
                Icon = BellOff;
                colorCls = 'border-[#4B6B52] text-[#4B6B52]';
              }
              return (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleEnableNotifications}
                  className={`rounded-none font-mono text-xs ${colorCls}`}
                  data-testid="notification-toggle"
                  title={
                    notifStatus === 'ios-needs-pwa'
                      ? 'iOS Safari only supports notifications in Home Screen PWAs. Tap to learn more.'
                      : notifStatus === 'denied'
                        ? 'Notifications blocked. Update browser site settings.'
                        : label
                  }
                >
                  <Icon className="w-3 h-3 mr-1" />
                  {label}
                </Button>
              );
            })()}
            <Badge className={`${getStatusBadge(order.status)} border rounded-none font-mono text-xs uppercase`}>
              {formatOrderStatus(order.status)}
            </Badge>
          </div>
        </div>

        {/* Status Timeline */}
        {order.status !== 'cancelled' && (
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-6 mb-6">
            <h2 className="text-[#39FF14] font-mono text-sm font-bold mb-6 uppercase">ORDER_STATUS</h2>
            <div className="flex items-center justify-between">
              {orderStatuses.map((status, index) => {
                const Icon = status.icon;
                const currentIndex = getCurrentStatusIndex();
                const isCompleted = index <= currentIndex;
                const isCurrent = index === currentIndex;
                
                return (
                  <div key={status.key} className="flex-1 flex flex-col items-center relative">
                    {index > 0 && (
                      <div className={`absolute left-0 right-1/2 top-4 h-0.5 -translate-y-1/2 ${
                        index <= currentIndex ? 'bg-[#39FF14]' : 'bg-[#2A0F3D]'
                      }`} />
                    )}
                    {index < orderStatuses.length - 1 && (
                      <div className={`absolute left-1/2 right-0 top-4 h-0.5 -translate-y-1/2 ${
                        index < currentIndex ? 'bg-[#39FF14]' : 'bg-[#2A0F3D]'
                      }`} />
                    )}
                    <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center ${
                      isCompleted ? 'bg-[#39FF14] text-[#050003]' : 'bg-[#2A0F3D] text-[#4B6B52]'
                    } ${isCurrent ? 'ring-2 ring-[#39FF14] ring-offset-2 ring-offset-[#0C0612]' : ''}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className={`mt-2 text-xs font-mono ${
                      isCompleted ? 'text-[#39FF14]' : 'text-[#4B6B52]'
                    }`}>
                      {status.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ETA Display for Out for Delivery */}
        {order.status === 'out_for_delivery' && (
          <div className="bg-[#0C0612] border border-[#39FF14] p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Navigation className="w-5 h-5 text-[#39FF14] animate-pulse" />
                <h2 className="text-[#39FF14] font-mono text-sm font-bold uppercase">DELIVERY_IN_PROGRESS</h2>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchOrder}
                className="border-[#2A0F3D] text-[#39FF14] hover:border-[#39FF14] rounded-none text-xs"
                data-testid="refresh-location"
              >
                <RefreshCw className={`w-3 h-3 mr-1 ${etaLoading ? 'animate-spin' : ''}`} />
                REFRESH
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* ETA Card */}
              <div className="bg-[#050003] border border-[#39FF14] p-4 text-center">
                <p className="text-[#6B8E73] font-mono text-xs uppercase mb-2">ESTIMATED ARRIVAL</p>
                <p className="text-[#39FF14] font-mono text-3xl font-bold neon-text">
                  {etaLoading ? (
                    <span className="text-xl">CALCULATING...</span>
                  ) : etaData ? (
                    etaData.durationMinutes <= 1 ? 'ARRIVING' : `${etaData.durationMinutes} MIN`
                  ) : (
                    '-- MIN'
                  )}
                </p>
                <p className="text-[#4B6B52] font-mono text-xs mt-2">
                  {etaData ? (
                    <>
                      <Route className="w-3 h-3 inline mr-1" />
                      {etaData.distanceText} away
                    </>
                  ) : (
                    'Calculating route...'
                  )}
                </p>
              </div>

              {/* Driver Info Card */}
              <div className="bg-[#050003] border border-[#2A0F3D] p-4">
                <p className="text-[#6B8E73] font-mono text-xs uppercase mb-2">YOUR DRIVER</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#2A0F3D] flex items-center justify-center">
                    <Truck className="w-5 h-5 text-[#39FF14]" />
                  </div>
                  <div>
                    <p className="text-[#39FF14] font-mono font-bold">{order.driver_name}</p>
                    <p className="text-[#4B6B52] font-mono text-xs">En route to you</p>
                  </div>
                </div>
              </div>

              {/* Status Card */}
              <div className="bg-[#050003] border border-[#2A0F3D] p-4">
                <p className="text-[#6B8E73] font-mono text-xs uppercase mb-2">DELIVERY STATUS</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#39FF14]" />
                    <span className="text-[#A3C7A8] font-mono text-xs">Order confirmed</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#39FF14]" />
                    <span className="text-[#A3C7A8] font-mono text-xs">Picked up by driver</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border border-[#39FF14] flex items-center justify-center">
                      <span className="w-2 h-2 bg-[#39FF14] status-pulse" />
                    </div>
                    <span className="text-[#39FF14] font-mono text-xs">On the way</span>
                  </div>
                </div>
              </div>
            </div>

            {showDriverLocation && order.driver_location && (
              <div className="mt-4 p-3 bg-[#050003] border border-[#2A0F3D]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#39FF14]" />
                    <span className="text-[#A3C7A8] font-mono text-xs">
                      Driver Location: {Number(order.driver_location.lat ?? 0).toFixed(5)}, {Number(order.driver_location.lng ?? 0).toFixed(5)}
                    </span>
                  </div>
                  {lastLocationUpdate && (
                    <span className="text-[#4B6B52] font-mono text-xs">
                      Updated: {lastLocationUpdate.toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {showDriverLocation && !order.driver_location && (
              <div className="mt-4 p-3 bg-[#050003] border border-[#FF9F0A]">
                <p className="text-[#FF9F0A] font-mono text-xs">
                  <Clock className="w-3 h-3 inline mr-1" />
                  Waiting for driver to share location...
                </p>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Order Items */}
          <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
            <h2 className="text-[#39FF14] font-mono text-sm font-bold mb-4 uppercase flex items-center gap-2">
              <Package className="w-4 h-4" />
              ORDER_ITEMS
            </h2>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex justify-between items-center py-2 border-b border-[#2A0F3D] last:border-0">
                  <div>
                    <p className="text-[#A3C7A8] font-mono text-sm" data-testid={`order-detail-item-${index}`}>
                      {item.product_name}
                      {item.size && (
                        <span
                          className="ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wider border border-[#FFD60A] text-[#FFD60A] rounded-none"
                          data-testid={`order-detail-item-size-${index}`}
                        >
                          {item.size}
                        </span>
                      )}
                    </p>
                    <p className="text-[#4B6B52] font-mono text-xs">Qty: {item.quantity}</p>
                    <div className="mt-1"><ReviewItemTag order={order} productId={item.product_id} index={index} /></div>
                  </div>
                  <span className="text-[#39FF14] font-mono">${(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
              <OrderFeeRows order={order} />
              <div className="pt-3 flex justify-between items-center">
                <span className="text-[#39FF14] font-mono font-bold">TOTAL</span>
                <span className="text-[#39FF14] font-mono font-bold text-lg">${Number(order.total || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Delivery Details */}
          <div className="space-y-6">
            <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
              <h2 className="text-[#39FF14] font-mono text-sm font-bold mb-4 uppercase flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                DELIVERY_ADDRESS
              </h2>
              <p className="text-[#A3C7A8] font-mono text-sm">{order.delivery_address}</p>
            </div>

            {order.delivery_slot && (
              <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
                <h2 className="text-[#39FF14] font-mono text-sm font-bold mb-4 uppercase flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  DELIVERY_TIME
                </h2>
                <p className="text-[#A3C7A8] font-mono text-sm" data-testid="order-delivery-time">{slotWhen(order.delivery_slot)}</p>
              </div>
            )}

            <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
              <h2 className="text-[#39FF14] font-mono text-sm font-bold mb-4 uppercase flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                PAYMENT_METHOD
              </h2>
              <p className="text-[#A3C7A8] font-mono text-sm uppercase">{order.payment_method}</p>
            </div>

            {order.driver_name && (
              <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
                <h2 className="text-[#39FF14] font-mono text-sm font-bold mb-4 uppercase flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  DRIVER_INFO
                </h2>
                <p className="text-[#A3C7A8] font-mono text-sm">{order.driver_name}</p>
              </div>
            )}

            {order.notes && (
              <div className="bg-[#0C0612] border border-[#2A0F3D] p-6">
                <h2 className="text-[#39FF14] font-mono text-sm font-bold mb-4 uppercase">ORDER_NOTES</h2>
                <p className="text-[#A3C7A8] font-mono text-sm">{order.notes}</p>
              </div>
            )}
          </div>
        </div>

        {/* Cancel Button */}
        {order.status === 'pending' && (
          <div className="mt-6">
            <Button 
              onClick={handleCancelOrder}
              variant="outline"
              className="border-[#FF3B30] text-[#FF3B30] hover:bg-[rgba(255,59,48,0.1)] rounded-none"
              data-testid="cancel-order"
            >
              <XCircle className="w-4 h-4 mr-2" />
              CANCEL ORDER
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
